import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import VersionCard from './components/VersionCard';
import RatingModal from './components/RatingModal';

const AppContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #1d1d1f;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #86868b;
`;

const VersionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const ErrorContainer = styled.div`
  background-color: #fff0f0;
  border: 1px solid #ffcccc;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
`;

const RetryButton = styled.button`
  background-color: #0071e3;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 10px;
`;

const DebugContainer = styled.div`
  margin-top: 20px;
  padding: 15px;
  background-color: #f5f5f7;
  border-radius: 8px;
  border: 1px solid #d1d1d6;
`;

// Sample data to use if API fails
const SAMPLE_DATA = [
  {"version":"18.4","total_ratings":"5","average_score":"4.50"},
  {"version":"18.3.2","total_ratings":"2","average_score":"4.50"},
  {"version":"18.3.1","total_ratings":"4","average_score":"4.50"},
  {"version":"18.3","total_ratings":"3","average_score":"4.50"},
  {"version":"18.2.1","total_ratings":"2","average_score":"4.50"},
  {"version":"18.2","total_ratings":"5","average_score":"4.50"},
  {"version":"18.1.1","total_ratings":"4","average_score":"4.50"},
  {"version":"18.1","total_ratings":"3","average_score":"4.50"},
  {"version":"18.0.1","total_ratings":"1","average_score":"4.50"},
  {"version":"18.0","total_ratings":"2","average_score":"3.50"}
];

function App() {
  const [versions, setVersions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState('');
  const [useSampleData, setUseSampleData] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorDetails('');
      
      // Use https instead of http
      const response = await axios.get('https://xieyonglin.com/ios-rating/ratings.php');
      
      if (response.data && Array.isArray(response.data)) {
        setVersions(response.data);
      } else {
        throw new Error('Invalid data format received from API');
      }
    } catch (err) {
      console.error('Error fetching version ratings:', err);
      let errorMsg = 'Failed to load iOS versions. Please try again later.';
      let details = '';
      
      if (err.response) {
        // The request was made and the server responded with a status code
        details = `Server responded with status ${err.response.status}: ${err.response.statusText}`;
      } else if (err.request) {
        // The request was made but no response was received
        details = 'No response received from server. This could be a network issue or CORS problem.';
      } else {
        // Something happened in setting up the request
        details = `Error: ${err.message}`;
      }
      
      setError(errorMsg);
      setErrorDetails(details);
    } finally {
      setLoading(false);
    }
  };

  const handleUseSampleData = () => {
    setVersions(SAMPLE_DATA);
    setUseSampleData(true);
    setError(null);
    setErrorDetails('');
  };

  const handleRateClick = (version) => {
    setSelectedVersion(version);
    setIsModalOpen(true);
  };

  const handleRatingSubmit = async (score) => {
    try {
      if (useSampleData) {
        // 使用模拟数据时的模拟提交逻辑
        setTimeout(() => {
          setIsModalOpen(false);
          alert('Rating submitted successfully (sample mode)');
        }, 500);
        return;
      }

      // 确保评分是整数
      const integerScore = Math.round(score);
      
      // POST 请求
      await axios.post('https://xieyonglin.com/ios-rating/rate.php', {
        version: selectedVersion,
        score: integerScore
      });
      
      setIsModalOpen(false);
      alert('Rating submitted successfully!');
      
      // 刷新数据
      fetchVersions();
    } catch (err) {
      console.error('Error submitting rating:', err);
      let errorMsg = 'Each IP address can only rate a version once. ';
      
      if (err.response) {
        errorMsg += `Server responded with status ${err.response.status}.`;
        console.log('Server response:', err.response.data);
        setDebugInfo({
          ...debugInfo,
          responseError: {
            status: err.response.status,
            data: err.response.data
          }
        });
      } else if (err.request) {
        errorMsg += 'No response received from server.';
        setDebugInfo({
          ...debugInfo,
          requestError: 'No response received'
        });
      } else {
        errorMsg += err.message;
        setDebugInfo({
          ...debugInfo,
          otherError: err.message
        });
      }
      
      alert(errorMsg);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const clearDebugInfo = () => {
    setDebugInfo(null);
  };

  if (loading) {
    return (
      <AppContainer>
        <Header>
          <Title>iOS Version Ratings</Title>
        </Header>
        <p>Loading versions...</p>
      </AppContainer>
    );
  }

  if (error) {
    return (
      <AppContainer>
        <Header>
          <Title>iOS Version Ratings</Title>
        </Header>
        <ErrorContainer>
          <p><strong>{error}</strong></p>
          {errorDetails && <p>{errorDetails}</p>}
          <RetryButton onClick={fetchVersions}>Try Again</RetryButton>
          <RetryButton onClick={handleUseSampleData} style={{marginLeft: '10px'}}>
            Use Sample Data
          </RetryButton>
        </ErrorContainer>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Header>
        <Title>iOS Version Ratings</Title>
        <Subtitle>Rate your experience with different iOS versions — and decide whether it’s worth the upgrade.</Subtitle>
        {useSampleData && (
          <p style={{color: '#ff6b00', fontSize: '0.9rem'}}>
            Using sample data. API connectivity is not available.
          </p>
        )}
      </Header>

      <VersionGrid>
        {versions.map((version) => (
          <VersionCard
            key={version.version}
            version={version.version}
            averageScore={version.avg_score}
            totalRatings={version.rating_count}
            onRateClick={() => handleRateClick(version.version)}
          />
        ))}
      </VersionGrid>

      {isModalOpen && (
        <RatingModal
          version={selectedVersion}
          onSubmit={handleRatingSubmit}
          onClose={handleCloseModal}
        />
      )}

    </AppContainer>
  );
}

export default App; 
